import {MAIN_MENU, MENU_TO_BOARD_MAP, MenuItem} from './menuConfig'

export type ChatbotMode = 'general' | 'attendance' | 'faq'

const ATTENDANCE_MENU_IDS = new Set(['attendance', 'attendance.records', 'attendance.stats'])

/** Boards served from the markdown knowledge base — the FAQ bot's whole world. */
const DOCUMENT_BOARDS = new Set(['document-context', 'document-vector', 'walkthrough'])

export const DEFAULT_ATTENDANCE_MENU_PATH = 'attendance'

export const DEFAULT_FAQ_MENU_PATH = 'faq'

export function isDocumentMenuPath(menuPath: string | null | undefined): boolean {
  if (!menuPath) return false
  return DOCUMENT_BOARDS.has(MENU_TO_BOARD_MAP[menuPath]?.board)
}

export function filterMenuByMode(mode: ChatbotMode, items: MenuItem[]): MenuItem[] {
  if (mode === 'general') {
    return items
  }

  const keep = (item: MenuItem): boolean =>
    mode === 'faq' ? isDocumentMenuPath(item.id) : ATTENDANCE_MENU_IDS.has(item.id)

  return items.filter(keep).map((item) => ({
    ...item,
    submenus: item.submenus ? filterMenuByMode(mode, item.submenus) : undefined,
  }))
}

export function getMainMenuForMode(mode: ChatbotMode): MenuItem[] {
  if (mode === 'general') {
    return MAIN_MENU
  }

  return []
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function showsAttendanceSubmenuAtRoot(_mode: ChatbotMode): boolean {
  return false
}

export function isAttendanceMenuPath(menuPath: string | null | undefined): boolean {
  if (!menuPath) return false
  return Boolean(MENU_TO_BOARD_MAP[menuPath]?.board === 'attendance-query')
}

export function resolveMenuPathForApi(
  mode: ChatbotMode,
  menuSelectedPath: string | null | undefined
): string | null {
  if (mode === 'faq') {
    // Pin every FAQ-mode query to a document board so free text always hits the
    // knowledge base instead of whatever the dispatcher would guess.
    if (menuSelectedPath && isDocumentMenuPath(menuSelectedPath)) {
      return menuSelectedPath
    }
    return DEFAULT_FAQ_MENU_PATH
  }

  if (mode !== 'attendance') {
    return menuSelectedPath ?? null
  }

  if (menuSelectedPath && isAttendanceMenuPath(menuSelectedPath)) {
    return menuSelectedPath
  }

  return DEFAULT_ATTENDANCE_MENU_PATH
}

/**
 * Adds the board this turn is for.
 *
 * `menu_selected` is deliberately NOT sent: the backend no longer reads it — it is not
 * validated and not routed on, and the ~50-key menu map it indexed is gone. Steering a turn is
 * `options.capabilityId` now (see `sendChatMessage`), which the backend revalidates against its
 * capability registry and re-applies the permission clamp to.
 */
export function applyBoardMappingToBody(
  body: Record<string, unknown>,
  menuPath: string | null
): Record<string, unknown> {
  if (!menuPath) {
    return body
  }

  const boardMapping = MENU_TO_BOARD_MAP[menuPath]
  if (!boardMapping) {
    return body
  }

  body.query_type = boardMapping.type
  body.board = boardMapping.board
  body.subType = boardMapping.subType
  return body
}

export function isWalkthroughEnabled(mode: ChatbotMode): boolean {
  return mode === 'general' || mode === 'faq'
}

export function getWelcomeMessageId(mode: ChatbotMode): string {
  if (mode === 'attendance') return 'CHATBOT.ATTENDANCE.WELCOME'
  if (mode === 'faq') return 'CHATBOT.FAQ.WELCOME'
  return 'CHATBOT.WELCOME'
}
